import React, { useEffect, useState } from "react";
import { useHostPortalContext } from "context/HostPortalContext";
import { useUser } from "context/UserContext";
import { useAlertContext } from "context/AlertContext";
import corporateAccountService from "services/corporateAccountService";
import locationsService from "services/locationsService";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { formatDate } from "../Utils";
import { useNavigate } from "react-router";
import LoadingSpinner from "./LoadingSpinner";

export interface CorporateDoorLockIssueCount {
  hostId: number;
  hostName: string;
  totalIssues: number;
  lastReportedAt: string | null;
}

interface CorporateDoorLockIssuesProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
}

const CorporateDoorLockIssues = (props: CorporateDoorLockIssuesProps) => {
   const { limitToId, description, title, hideBusinessName, disableClick } = props;
  const navigate = useNavigate();
  const { setHost } = useHostPortalContext();
  const { userCorporateAccount } = useUser();
  const { setAlert } = useAlertContext();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [issues, setIssues] = useState<CorporateDoorLockIssueCount[]>([]);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);

  useEffect(() => {
    const loadIssues = async () => {
      setLoading(true);
      try {
        let data = await corporateAccountService.getCorporateHostDoorLockIssueCounts(
          userCorporateAccount?.id
        );
        if (data) {
            if(limitToId){
                data = data.filter(a => a.hostId === limitToId);
            }

            setIssues(data);
            setShowCorporateRollup(data.filter((a) => a.hostId !== -1).length > 1);
        } else {
          setAlert("error", "Unable to load door lock issues");
        }
      } catch (err) {
        console.error(err);
        setAlert("error", "Error loading door lock issues");
      } finally {
        setLoading(false);
      }
    };
    loadIssues();
  }, [userCorporateAccount]);

  const goToManageHost = async (hostId: number) => {
    if (disableClick || hostId === -1) return;
    setRedirecting(true);
    const selectedHost = await locationsService.getHostRecord(hostId);
    if (!selectedHost) {
      setRedirecting(false);
      return;
    }
    setHost(selectedHost);
    setRedirecting(false);
    navigate(`/location/${hostId}`);
  };

  const renderIssueItem = (item: CorporateDoorLockIssueCount) => {
    const isTotalRow = item.hostId === -1;
    if (isTotalRow && !showCorporateRollup) return null;

    return (
      <Card
        key={item.hostId}
        sx={{
          p: 2,
          mb: 2,
          cursor: isTotalRow ? "default" : "pointer",
          backgroundColor: isTotalRow ? "#e0f7fa" : "#fff",
          width: "100%",
        }}
        onClick={() => goToManageHost(item.hostId)}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="bold">
            {isTotalRow ? "Corporate Total" : item.hostName}
          </MDTypography>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" mt={2}>
          <MDBox textAlign="center">
            <MDTypography variant="h6">
              {item.lastReportedAt ? formatDate(item.lastReportedAt, true) : "-"}
            </MDTypography>
            <MDTypography variant="caption">Most Recent Issue</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">{item.totalIssues}</MDTypography>
            <MDTypography variant="caption">Total Issues</MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  return (
      <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
           {title ?? 'Door Lock Issues'}
          </MDTypography>
       <MDTypography variant="button" color="text" mb={1} sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}> 
            {description ?? 'Reported by users when the provided door lock does not work or is inaccessible.'}
          </MDTypography>

          {redirecting || loading ? (
            <LoadingSpinner />
          ) : issues.length === 0 ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" mt={4}>
              <MDTypography>No door lock issues reported.</MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              {issues.map((item) => renderIssueItem(item))}
            </MDBox>
          )}
      </MDBox>
  );
};

export default CorporateDoorLockIssues;
